import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Determine if we're on a dark page (home hero)
  const isDarkPage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    {
      name: 'Services',
      href: '/services',
      children: [
        { name: 'Luminarc™', href: '/luminarc' },
        { name: 'DPDP Compliance', href: '/services/dpdp-compliance' },
        { name: 'Consulting & Strategy', href: '/services/consulting' },
        { name: 'Corporate Training', href: '/services/corporate-training' },
        { name: 'Coaching & Mentorship', href: '/services/coaching' },
      ],
    },
    { name: 'Testimonials', href: '/testimonials' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (href) => location.pathname === href;

  const headerClass = isScrolled || !isDarkPage
    ? 'bg-white border-b border-brand-lavender text-brand-charcoal'
    : 'bg-transparent text-white';

  return (
    <header
      className={`fixed w-full z-50 transition-all duration-300 ${headerClass}`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3 group"
            data-testid="header-logo"
          >
            <img
              src={isScrolled || !isDarkPage ? '/assets/images/logo-purple.png' : '/assets/images/logo-white.png'}
              alt="Expertize Edge"
              className="h-10 w-10 transition-transform duration-300 group-hover:scale-105"
            />
            <span className={`font-logo text-lg tracking-wider hidden sm:block ${
              isScrolled || !isDarkPage ? 'text-brand-purple' : 'text-white'
            }`}>
              EXPERTIZE EDGE
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <div key={link.name} className="relative group">
                {link.children ? (
                  <div
                    className="relative"
                    onMouseEnter={() => setIsServicesOpen(true)}
                    onMouseLeave={() => setIsServicesOpen(false)}
                  >
                    <button
                      className={`flex items-center gap-1 font-medium transition-colors duration-200 hover:text-brand-gold ${
                        isActive(link.href) ? 'text-brand-purple' : ''
                      }`}
                      data-testid="services-dropdown"
                    >
                      {link.name}
                      <ChevronDown className="w-4 h-4 transition-transform duration-200 group-hover:rotate-180" />
                    </button>
                    <AnimatePresence>
                      {isServicesOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-brand-lavender overflow-hidden"
                        >
                          {link.children.map((child) => (
                            <Link
                              key={child.href}
                              to={child.href}
                              className="block px-4 py-3 text-brand-body hover:bg-brand-lavender hover:text-brand-purple transition-colors duration-200"
                              data-testid={`nav-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {child.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    to={link.href}
                    className={`font-medium transition-colors duration-200 hover:text-brand-gold ${
                      isActive(link.href) ? 'text-brand-purple' : ''
                    }`}
                    data-testid={`nav-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            {user ? (
              <Link
                to="/portal"
                className="bg-brand-gold text-brand-charcoal font-semibold px-6 py-2.5 hover:bg-brand-gold-lite transition-all duration-300"
                data-testid="portal-button"
              >
                Client Portal
              </Link>
            ) : (
              <button
                onClick={() => navigate('/contact')}
                className="bg-brand-gold text-brand-charcoal font-semibold px-6 py-2.5 hover:bg-brand-gold-lite transition-all duration-300"
                data-testid="book-call-button"
              >
                Book a Call
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-brand-lavender"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 space-y-4">
              {navLinks.map((link) => (
                <div key={link.name}>
                  {link.children ? (
                    <div className="space-y-2">
                      <Link
                        to={link.href}
                        className="block font-semibold text-brand-charcoal"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {link.name}
                      </Link>
                      <div className="pl-4 space-y-2">
                        {link.children.map((child) => (
                          <Link
                            key={child.href}
                            to={child.href}
                            className="block text-brand-muted hover:text-brand-purple transition-colors"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Link
                      to={link.href}
                      className={`block font-medium ${
                        isActive(link.href) ? 'text-brand-purple' : 'text-brand-charcoal'
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {link.name}
                    </Link>
                  )}
                </div>
              ))}
              <div className="pt-4 border-t border-brand-lavender space-y-3">
                {user ? (
                  <Link
                    to="/portal"
                    className="block w-full text-center bg-brand-gold text-brand-charcoal font-semibold px-6 py-3"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Client Portal
                  </Link>
                ) : (
                  <Link
                    to="/contact"
                    className="block w-full text-center bg-brand-gold text-brand-charcoal font-semibold px-6 py-3"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Book a Call
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
